import React, { useEffect, useRef, useState, useContext } from "react";
import { HiOutlineSearch } from "react-icons/hi";
import { ChatbotContext } from "../../TabContextProvider";
import ChatbotApiClient from "../../../../methods/api/chatbotapiClient";
import { useSelector } from "react-redux";
import DOMPurify from "dompurify";
import parse from "html-react-parser";
import methodModel from "../../../../methods/methods";
import datepipeModel from "../../../../models/datepipemodel";
import loader from "../../../../methods/loader";
import PaginationTextView from "../../../../components/common/PaginationTextView";
import Pagination from "react-pagination-js";
import moment from "moment";
import "./chat-log.css";
import { BsArrowLeft } from "react-icons/bs";

const Index = ({ VisitorId }) => {
  const { chatbotAppearanceData } = useContext(ChatbotContext);
  const user = useSelector((state) => state.user);

  const timeoutRef = useRef(null);

  const [chatbotFilter, setChatbotFilter] = useState({ page: 1, limit: 20 });
  const [visitorList, setVisitorList] = useState([]);
  const [filteredVisitorList, setFilteredVisitorList] = useState([]);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [visitorHistory, setVisitorHistory] = useState([]);
  const [currentVisitorId, setCurrentVisitorId] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");

  /* ------------------ SEARCH ------------------ */
  const handleSearchChange = (e) => setSearch(e.target.value);

  useEffect(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      getList();
    }, 600);
  }, [search]);

  /* ------------------ FETCH VISITORS ------------------ */
  const getList = (p = {}) => {
    loader(true);
    const filter = { ...chatbotFilter, ...p };

    ChatbotApiClient.get("visitors/list", {
      userId: user?.id || user?._id,
      search,
      ...filter,
    }).then((res) => {
      if (res.success) {
        const sorted = res?.data?.sort(
          (a, b) =>
            new Date(b?.latest_answer_datetime) -
            new Date(a?.latest_answer_datetime)
        );

        setVisitorList(sorted);
        setFilteredVisitorList(sorted);
        setTotalCount(res?.pagination?.total);
      }
      loader(false);
    });
  };

  useEffect(() => {
    getList();
  }, []);

  /* ------------------ SELECT VISITOR ------------------ */
  const handleVisitorClick = (visitorId, scroll = false) => {
    setSelectedVisitor(visitorId);

    if (currentVisitorId !== visitorId) {
      loader(true);
      ChatbotApiClient.get("question/list", { visitorId }).then((res) => {
        if (res.success) {
          setVisitorHistory(res.data);
          setCurrentVisitorId(visitorId);

          if (scroll) {
            setTimeout(() => {
              try {
                document.getElementById(visitorId)?.scrollIntoView({
                  behavior: "auto",
                });
              } catch {}
            }, 200);
          }
        }
        loader(false);
      });
    }
  };

  useEffect(() => {
    if (VisitorId) handleVisitorClick(VisitorId, true);
  }, [VisitorId]);

  /* ------------------ PAGINATION ------------------ */
  const pageChange = (page) => {
    setChatbotFilter((prev) => ({ ...prev, page }));
    getList({ page });
  };

  /* ------------------ RENDER ------------------ */
  return (
    <div className="max-w-[1400px]">
      <div className="bg-white border border-gray-5 rounded-large flex max-md:flex-wrap">

        {/* ---------------- LEFT PANEL ---------------- */}
        <div className="md:min-w-[330px] w-full md:w-[330px] border-r border-gray-5">

          <div className="px-5 py-3 border-b border-gray-5">
            <h4 className="text-base font-medium text-[#2B2B2B] mb-5">
              Chat History
            </h4>

            <div className="relative flex items-center gap-4">
              <img
                src={methodModel.userImg(user?.img || user?.image || user?.logo)}
                className="w-10 h-10 rounded-full"
                alt=""
              />

              <input
                onChange={handleSearchChange}
                className="pl-10 h-10 rounded-full border-[1.5px] border-[#D0D5DD] text-sm w-full focus:ring-1 focus:ring-[#115B9D]"
                placeholder="Search"
              />

              <HiOutlineSearch className="absolute left-[70px] top-3 text-gray-600" />
            </div>
          </div>

          <div className="overflow-auto no-scrollbar" style={{ maxHeight: 516 }}>
            {filteredVisitorList.map((item, index) => {
              const isActive = selectedVisitor === item?.visitorId;

              return (
                <div
                  key={index}
                  id={item?.visitorId}
                  onClick={() => handleVisitorClick(item?.visitorId)}
                  className={`p-3 ps-5 border-b flex gap-2 cursor-pointer transition-all
                    ${isActive ? "bg-[#F6F4FF]" : "hover:bg-gray-50"}`}
                >
                  <img
                    src="/assets/img/logo.png"
                    className="w-8 h-8 rounded-full"
                    alt=""
                  />

                  <div className="w-full">
                    <div className="flex justify-between">
                      <h5 className={`text-sm font-semibold capitalize line-clamp-1 ${isActive ? "text-black" : ""}`}>
                        {item?.name || item.visitorId}
                      </h5>
                    </div>

                    <p className={`text-xs text-gray-500 line-clamp-2 ${isActive ? "text-black" : ""}`}>
                      {item.location}
                    </p>

                    <div className={`text-[12px] mt-1 ${isActive ? "text-black" : "text-gray-600"}`}>
                      <span>
                        {datepipeModel.date(item?.latest_answer_datetime?.split("T")[0])}
                      </span>{" "}
                      <span>
                        {datepipeModel.time(item?.latest_answer_datetime?.split("T")[1])}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredVisitorList.length === 0 && (
              <div className="text-center p-3">No Data</div>
            )}

            {totalCount > chatbotFilter.limit && (
              <div className="flex justify-between p-3">
                <p className="text-sm text-gray-600">
                  {PaginationTextView(
                    chatbotFilter.page,
                    chatbotFilter.limit,
                    totalCount
                  )}
                </p>

                <Pagination
                  currentPage={chatbotFilter.page}
                  totalSize={totalCount}
                  sizePerPage={chatbotFilter.limit}
                  changeCurrentPage={pageChange}
                />
              </div>
            )}
          </div>
        </div>

        {/* ---------------- RIGHT PANEL ---------------- */}
        <div className="w-full flex-1">

          <div className="flex px-6 border-b items-center justify-between">
            <div className="flex items-center gap-2 py-3">
              <BsArrowLeft className="md:hidden text-xl cursor-pointer hover:opacity-80" />
              <img src="/assets/img/logo.png" className="object-contain w-6 h-6" alt="" />
              <h6 className="text-base font-medium">IKnow</h6>
              <span className="border px-1.5 rounded text-xs flex items-center gap-1">
                <img src="/assets/img/svgs/stars.svg" className="w-3 h-3" />
                AI
              </span>
            </div>
          </div>

          {/* ---------------- CHAT AREA ---------------- */}
          <div className="overflow-auto p-5 scrollbar" style={{ maxHeight: 563 }}>

            {visitorHistory.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-600 text-sm font-medium">
                No chats to show.
              </div>
            ) : (
              visitorHistory.map((item, index) => (
                <div key={index}>

                  {/* User */}
                  <div className="flex justify-end mb-2 pr-6">
                    <p
                      className="text-sm rounded-2xl px-4 py-3"
                      style={{
                        backgroundColor:
                          chatbotAppearanceData?.user_message_background || "#6366f1",
                        color:
                          chatbotAppearanceData?.user_message_color || "#fff",
                      }}
                    >
                      {item.question}
                    </p>
                  </div>

                  {/* Bot */}
                  <div className="flex gap-2 mb-3 pr-16">
                    <img src="/assets/img/logo.png" className="w-8 h-8" />

                    <div>
                      <p className="text-[11px] text-gray-600">
                        {moment(item?.createdAt?.split("T")[0]).format("DD MMM")},{" "}
                        {datepipeModel.time(item?.createdAt?.split("T")[1])}
                      </p>

                      <p
                        className="text-sm rounded-2xl px-4 py-3"
                        style={{
                          backgroundColor:
                            chatbotAppearanceData?.bot_message_background || "#f3f4f6",
                          color:
                            chatbotAppearanceData?.bot_message_color || "#000",
                        }}
                      >
                        {parse(
                          methodModel.textPatternFormatting(
                            DOMPurify.sanitize(item.answer)
                          )
                        )}
                      </p>
                    </div>
                  </div>

                </div>
              ))
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;